import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  formatDistance, formatSpeed, formatVolume, formatTime, formatNumericHours,
} from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import ColumnSelect from './components/ColumnSelect';
import usePersistedState from '../common/util/usePersistedState';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import MapRoutePath from '../map/MapRoutePath';
import AddressValue from '../common/components/AddressValue';
import TableShimmer from '../common/components/TableShimmer';
import MapMarkers from '../map/MapMarkers';
import scheduleReport from './common/scheduleReport';
import ReportMapPanel from './components/ReportMapPanel';
import ReportMapContent from './components/ReportMapContent';
import fetchOrThrow from '../common/util/fetchOrThrow';
import exportExcel from '../common/util/exportExcel';
import exportTablePdf from '../common/util/exportTablePdf';

const columnsArray = [
  ['startTime', 'reportStartTime'],
  ['startOdometer', 'reportStartOdometer'],
  ['startAddress', 'reportStartAddress'],
  ['endTime', 'reportEndTime'],
  ['endOdometer', 'reportEndOdometer'],
  ['endAddress', 'reportEndAddress'],
  ['distance', 'sharedDistance'],
  ['averageSpeed', 'reportAverageSpeed'],
  ['maxSpeed', 'reportMaximumSpeed'],
  ['duration', 'reportDuration'],
  ['spentFuel', 'reportSpentFuel'],
  ['driverName', 'sharedDriver'],
];
const columnsMap = new Map(columnsArray);

const TripReportPage = () => {
  const navigate = useNavigate();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const devices = useSelector((state) => state.devices.items);
  const drivers = useSelector((state) => state.drivers.items);

  const distanceUnit = useAttributePreference('distanceUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const [columns, setColumns] = usePersistedState('tripColumns', ['startTime', 'endTime', 'distance', 'averageSpeed']);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [route, setRoute] = useState(null);

  const createMarkers = (item) => {
    if (!item) {
      return [];
    }

    return [
      {
        latitude: item.startLat,
        longitude: item.startLon,
        image: 'start-success',
      },
      {
        latitude: item.endLat,
        longitude: item.endLon,
        image: 'finish-error',
      },
    ];
  };

  useEffectAsync(async () => {
    if (selectedItem) {
      const query = new URLSearchParams({
        deviceId: selectedItem.deviceId,
        from: selectedItem.startTime,
        to: selectedItem.endTime,
      });
      const response = await fetchOrThrow(`/api/reports/route?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      setRoute(await response.json());
    } else {
      setRoute(null);
    }
  }, [selectedItem]);

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/trips?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  });

  const onExport = useCatch(async ({ format = 'xlsx' } = {}) => {
    const sheets = new Map();

    items.forEach((item) => {
      const deviceName = devices[item.deviceId].name;
      if (!sheets.has(deviceName)) {
        sheets.set(deviceName, []);
      }

      const row = {};
      columns.forEach((key) => {
        const header = t(columnsMap.get(key));
        if (key === 'startAddress') {
          row[header] = item.startAddress || '';
        } else if (key === 'endAddress') {
          row[header] = item.endAddress || '';
        } else {
          row[header] = formatValue(item, key);
        }
      });

      sheets.get(deviceName).push(row);
    });

    const normalizedFormat = String(format).toLowerCase();
    if (normalizedFormat === 'pdf') {
      const sheetRows = Array.from(sheets.values()).flat();
      const pdfColumns = columns.map((key) => t(columnsMap.get(key)));
      await exportTablePdf(t('reportTrips'), 'trips.pdf', pdfColumns, sheetRows);
      return;
    }

    await exportExcel(t('reportTrips'), 'trips.xlsx', sheets, theme);
  });


  const onOpenReplay = (item) => {
    const query = new URLSearchParams({
      deviceId: String(item.deviceId),
      from: item.startTime,
      to: item.endTime,
    });
    navigate(`/replay?${query.toString()}`);
  };

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'trips';
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const getDriverDisplayName = (item) => {
    if (item.driverUniqueId && drivers[item.driverUniqueId]?.name) {
      return drivers[item.driverUniqueId].name;
    }

    return item.driverUniqueId || '';
  };

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value].name;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
      case 'endOdometer':
      case 'distance':
        return formatDistance(value, distanceUnit, t);
      case 'averageSpeed':
      case 'maxSpeed':
        return value > 0 ? formatSpeed(value, speedUnit, t) : null;
      case 'duration':
        return formatNumericHours(value, t);
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : null;
      case 'driverName':
        return getDriverDisplayName(item);
      case 'startAddress':
        return (<AddressValue latitude={item.startLat} longitude={item.startLon} originalAddress={value} />);
      case 'endAddress':
        return (<AddressValue latitude={item.endLat} longitude={item.endLon} originalAddress={value} />);
      default:
        return value;
    }
  };

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportTrips']}>
      <div className={classes.container}>
        <ReportMapPanel
          title={t('reportTrips')}
          show={Boolean(selectedItem)}
          onClearSelection={selectedItem ? () => setSelectedItem(null) : null}
          onExpandedChange={setMapExpanded}
        >
          <ReportMapContent camera={Boolean(route)} cameraProps={route ? { positions: route } : {}}>
            {route && selectedItem && (
              <>
                <MapRoutePath positions={route} />
                <MapMarkers markers={createMarkers(selectedItem)} />
              </>
            )}
          </ReportMapContent>
        </ReportMapPanel>
        <div
          className={classes.containerMain}
          data-has-map={Boolean(selectedItem)}
          data-map-expanded={mapExpanded}
        >
          <div className={classes.header}>
            <ReportFilter onShow={onShow} onExport={onExport} onSchedule={onSchedule} deviceType="multiple" loading={loading}>
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          <div className={classes.tableCard}>
            <div className={classes.tableScroller} data-has-map={Boolean(selectedItem)} data-map-expanded={mapExpanded}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.columnAction} />
                    <TableCell>{t('sharedDevice')}</TableCell>
                    {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading ? (items.length ? items.map((item) => (
                    <TableRow key={item.startPositionId}>
                      <TableCell className={classes.columnAction} padding="none">
                        {selectedItem === item ? (
                          <IconButton size="small" onClick={() => setSelectedItem(null)}>
                            <GpsFixedIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => setSelectedItem(item)}>
                            <LocationSearchingIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Tooltip title="Abrir no replay com o mesmo período">
                          <IconButton size="small" onClick={() => onOpenReplay(item)}>
                            <PlayCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{devices[item.deviceId].name}</TableCell>
                      {columns.map((key) => (
                        <TableCell key={key}>
                          {formatValue(item, key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )) : (<TableRow><TableCell colSpan={columns.length + 2}>{t('reportNoData')}</TableCell></TableRow>)) : (<TableShimmer columns={columns.length + 2} startAction />)}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TripReportPage;
